import { nextInteger } from '../core'
import type {
  GridDimensions,
  GridPosition,
  LetterBoard,
  LetterCell,
} from '../core/types'
import { BOGGLE_SPECIAL_TILE_KINDS } from '~/boggle/config/specialTiles/kinds'
import type { BoggleSpecialTileKind } from '~/boggle/config/specialTiles/kinds'
import { BOGGLE_SPECIAL_TILE_REGISTRY } from '~/boggle/config/specialTiles/registry'
import type {
  BoggleSpecialTileCadenceCounts,
  BoggleSpecialTileVisualShape,
} from '~/boggle/config/specialTiles/tileTypes'

export {
  BOGGLE_BLUE_TILE_KIND,
  BOGGLE_GOLD_TILE_KIND,
  BOGGLE_PURPLE_TILE_KIND,
  BOGGLE_BLUE_TIME_BONUS_MS,
  BOGGLE_GOLD_SCORE_BONUS,
  BOGGLE_SPECIAL_TILE_KINDS,
} from '~/boggle/config/specialTiles/kinds'
export type { BoggleSpecialTileKind } from '~/boggle/config/specialTiles/kinds'

export type {
  BoggleBlueTileSettings,
  BoggleGoldTileSettings,
  BogglePlaygroundSpecialTileSettings,
  BogglePurpleTileSettings,
  BoggleSpecialTileCadenceCounts,
  BoggleSpecialTileDefinition,
  BoggleSpecialTileSettings,
  BoggleSpecialTileVisualShape,
} from '~/boggle/config/specialTiles/tileTypes'

export { DEFAULT_BOGGLE_PLAYGROUND_SPECIAL_TILE_SETTINGS } from '~/boggle/config/specialTiles/defaultPlaygroundSettings'
export { BOGGLE_SPECIAL_TILE_REGISTRY } from '~/boggle/config/specialTiles/registry'
export {
  buildEffectiveSpecialTileCounts,
  buildPhysicalSpecialTileCounts,
  createEmptySpecialTileCounts,
  getEffectiveGoldCount,
  getSpecialTilePreviewFill,
  getSpecialTilePreviewFillNumber,
  getSpecialTilePreviewTextColor,
  getSpecialTilePreviewTextColorNumber,
  getSpecialTilePreviewTextShadow,
  getVirtualGoldCount,
  hexColorToNumber,
  isEffectiveGoldPathStep,
  normalizeLegacyPathStep,
  resolveLiveCellDisplayKind,
  resolvePathStepDisplayKind,
  type BoggleAcceptedWordPathStep,
  type BoggleLegacyAcceptedWordPathStep,
  type BoggleSpecialTileCountRecord,
} from './display'

type CellWithTags = {
  tags?: readonly string[]
}

type SpecialTilePlacement = {
  col: number
  row?: number
  toCol?: number
  toRow?: number
}

function cloneBoard(board: LetterBoard) {
  return board.map(row => [...row])
}

function getPlacementPosition(placement: SpecialTilePlacement) {
  const row = placement.row ?? placement.toRow
  const col = placement.toCol ?? placement.col
  if (row === undefined) {
    return null
  }

  return { col, row }
}

function getPlacementCell(board: LetterBoard, placement: SpecialTilePlacement) {
  const position = getPlacementPosition(placement)
  if (position === null) {
    return null
  }

  return board[position.row]?.[position.col] ?? null
}

function isEligibleSpecialTilePlacement(
  board: LetterBoard,
  placement: SpecialTilePlacement,
) {
  const cell = getPlacementCell(board, placement)
  return cell !== null && !hasAnyBoggleSpecialTile(cell)
}

function withSpecialTileKind(
  tags: readonly string[] | undefined,
  kind: BoggleSpecialTileKind,
) {
  if (tags?.includes(kind)) {
    return tags
  }

  return [...(tags ?? []), kind]
}

function pickDistinctEntries<T>(entries: readonly T[], count: number, rngState: number) {
  const available = [...entries]
  const picked: T[] = []
  let currentRngState = rngState

  for (let index = 0; index < count && available.length > 0; index += 1) {
    const next = nextInteger(currentRngState, available.length)
    currentRngState = next.rngState
    picked.push(available.splice(next.value, 1)[0]!)
  }

  return {
    picked,
    rngState: currentRngState,
  }
}

export function getBoggleMaxSpecialTileCount(cellCount: number) {
  return Math.max(0, Math.floor(cellCount))
}

export function clampBoggleSpecialTileCount(count: number, maxCount: number) {
  if (!Number.isFinite(count)) {
    return 0
  }

  return Math.max(0, Math.min(maxCount, Math.floor(count)))
}

export function normalizeNullableSpawnCadence(value: null | number | undefined) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }

  return Math.max(1, Math.floor(value))
}

export function normalizeNullableSpecialTileSpawnChance(
  value: null | number | undefined,
) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }

  if (value <= 0) {
    return null
  }

  return Math.max(1, Math.min(100, Math.floor(value)))
}

export function normalizeNullableSpecialTileMaximumCount(
  value: null | number | undefined,
  maxCount: number,
) {
  if (value === null) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }

  return clampBoggleSpecialTileCount(value, maxCount)
}

export function normalizeNullableSpecialTileConvertCount(
  value: null | number | undefined,
  maxCount: number,
) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }

  return Math.max(1, clampBoggleSpecialTileCount(value, maxCount))
}

export function isBoggleSpecialTile(
  cell: CellWithTags | null | undefined,
  kind: BoggleSpecialTileKind,
) {
  return cell?.tags?.includes(kind) ?? false
}

export function countBoggleSpecialTiles(
  cells: ReadonlyArray<CellWithTags | null | undefined>,
  kind: BoggleSpecialTileKind,
) {
  return cells.reduce(
    (count, cell) => count + (isBoggleSpecialTile(cell, kind) ? 1 : 0),
    0,
  )
}

export function countBoggleSpecialTilesOnBoard(
  board: LetterBoard,
  kind: BoggleSpecialTileKind,
) {
  return countBoggleSpecialTiles(board.flat(), kind)
}

export function createEmptyBoggleSpecialTileCadenceCounts(): BoggleSpecialTileCadenceCounts {
  return {
    gold: 0,
    blue: 0,
    purple: 0,
  }
}

export function getBoggleSpecialTileScoreBonus(
  board: LetterBoard,
  path: readonly GridPosition[],
) {
  let bonus = 0

  for (const position of path) {
    const cell = board[position.row]?.[position.col]
    if (!cell) {
      continue
    }

    for (const definition of Object.values(BOGGLE_SPECIAL_TILE_REGISTRY)) {
      if (isBoggleSpecialTile(cell, definition.kind)) {
        bonus += definition.scoreBonusPerPathPosition
      }
    }
  }

  return bonus
}

export function getBoggleSpecialTileTimeBonusMs(
  board: LetterBoard,
  path: readonly GridPosition[],
) {
  let bonus = 0

  for (const definition of Object.values(BOGGLE_SPECIAL_TILE_REGISTRY)) {
    if (definition.timeBonusPerWordMs <= 0) {
      continue
    }

    bonus +=
      countBoggleSpecialTilesInPath(board, path, definition.kind) *
      definition.timeBonusPerWordMs
  }

  return bonus
}

export function hasBoggleSpecialTileInPath(
  board: LetterBoard,
  path: readonly GridPosition[],
  kind: BoggleSpecialTileKind,
) {
  return path.some(position =>
    isBoggleSpecialTile(board[position.row]?.[position.col], kind),
  )
}

export function isEveryBogglePathTileSpecial(
  board: LetterBoard,
  path: readonly GridPosition[],
  kind: BoggleSpecialTileKind,
) {
  if (path.length === 0) {
    return false
  }

  return path.every(position => {
    const cell = board[position.row]?.[position.col]
    return cell !== null && cell !== undefined && isBoggleSpecialTile(cell, kind)
  })
}

export function countBoggleSpecialTilesInPath(
  board: LetterBoard,
  path: readonly GridPosition[],
  kind: BoggleSpecialTileKind,
) {
  let count = 0

  for (const position of path) {
    if (isBoggleSpecialTile(board[position.row]?.[position.col], kind)) {
      count += 1
    }
  }

  return count
}

export function getBoggleSpecialTileVisualShape(
  cell: CellWithTags | null | undefined,
): BoggleSpecialTileVisualShape | null {
  if (!cell) {
    return null
  }

  for (const definition of Object.values(BOGGLE_SPECIAL_TILE_REGISTRY)) {
    if (isBoggleSpecialTile(cell, definition.kind)) {
      return definition.tileShape
    }
  }

  return null
}

export function hasAnyBoggleSpecialTile(cell: CellWithTags | null | undefined) {
  return (cell?.tags?.length ?? 0) > 0
}

function applySpecialTilePlacements<T extends SpecialTilePlacement>(
  board: LetterBoard,
  placements: readonly T[],
  kind: BoggleSpecialTileKind,
) {
  if (placements.length === 0) {
    return board
  }

  const nextBoard = cloneBoard(board)
  for (const placement of placements) {
    const position = getPlacementPosition(placement)
    if (position === null) {
      continue
    }

    const cell = nextBoard[position.row]?.[position.col]
    if (!cell) {
      continue
    }

    nextBoard[position.row]![position.col] = {
      ...cell,
      tags: withSpecialTileKind(cell.tags, kind),
    } satisfies LetterCell
  }

  return nextBoard
}

export function placeBoggleSpecialTilesOnBoard(
  board: LetterBoard,
  positions: readonly GridPosition[],
  count: number,
  kind: BoggleSpecialTileKind,
  rngState: number,
): {
  board: LetterBoard
  placements: GridPosition[]
  rngState: number
}
export function placeBoggleSpecialTilesOnBoard<T extends SpecialTilePlacement>(
  board: LetterBoard,
  positions: readonly T[],
  count: number,
  kind: BoggleSpecialTileKind,
  rngState: number,
): {
  board: LetterBoard
  placements: T[]
  rngState: number
}
export function placeBoggleSpecialTilesOnBoard<T extends SpecialTilePlacement>(
  board: LetterBoard,
  positions: readonly T[],
  count: number,
  kind: BoggleSpecialTileKind,
  rngState: number,
) {
  const eligiblePositions = positions.filter(position =>
    isEligibleSpecialTilePlacement(board, position),
  )
  const cappedCount = clampBoggleSpecialTileCount(count, eligiblePositions.length)
  const picked = pickDistinctEntries(eligiblePositions, cappedCount, rngState)

  return {
    board: applySpecialTilePlacements(board, picked.picked, kind),
    placements: picked.picked,
    rngState: picked.rngState,
  }
}

export function placeBoggleSpecialTilesOnSpawnedCells(
  board: LetterBoard,
  spawned: ReadonlyArray<SpecialTilePlacement & CellWithTags>,
  count: number,
  kind: BoggleSpecialTileKind,
  rngState: number,
): {
  board: LetterBoard
  rngState: number
  spawned: Array<SpecialTilePlacement & CellWithTags>
}
export function placeBoggleSpecialTilesOnSpawnedCells<
  T extends SpecialTilePlacement & CellWithTags,
>(
  board: LetterBoard,
  spawned: readonly T[],
  count: number,
  kind: BoggleSpecialTileKind,
  rngState: number,
): {
  board: LetterBoard
  rngState: number
  spawned: T[]
}
export function placeBoggleSpecialTilesOnSpawnedCells<
  T extends SpecialTilePlacement & CellWithTags,
>(
  board: LetterBoard,
  spawned: readonly T[],
  count: number,
  kind: BoggleSpecialTileKind,
  rngState: number,
) {
  const eligibleSpawned = spawned.filter(
    entry =>
      !hasAnyBoggleSpecialTile(entry) && isEligibleSpecialTilePlacement(board, entry),
  )
  const placement = placeBoggleSpecialTilesOnBoard(
    board,
    eligibleSpawned,
    count,
    kind,
    rngState,
  )
  if (placement.placements.length === 0) {
    return {
      board,
      rngState,
      spawned: [...spawned],
    }
  }

  const selected = new Set(
    placement.placements
      .map(getPlacementPosition)
      .filter(position => position !== null)
      .map(position => `${position.row}:${position.col}`),
  )
  return {
    board: placement.board,
    rngState: placement.rngState,
    spawned: spawned.map(entry => {
      const position = getPlacementPosition(entry)
      if (position === null || !selected.has(`${position.row}:${position.col}`)) {
        return entry
      }

      return {
        ...entry,
        tags: withSpecialTileKind(entry.tags, kind),
      }
    }),
  }
}

export function placeBoggleSpecialTilesOnSpawnChance(
  board: LetterBoard,
  spawned: ReadonlyArray<SpecialTilePlacement & CellWithTags>,
  settingsByKind: Record<
    BoggleSpecialTileKind,
    {
      maximumCount: null | number
      spawnChancePercent: null | number
    }
  >,
  rngState: number,
): {
  board: LetterBoard
  rngState: number
  spawned: Array<SpecialTilePlacement & CellWithTags>
}
export function placeBoggleSpecialTilesOnSpawnChance<
  T extends SpecialTilePlacement & CellWithTags,
>(
  board: LetterBoard,
  spawned: readonly T[],
  settingsByKind: Record<
    BoggleSpecialTileKind,
    {
      maximumCount: null | number
      spawnChancePercent: null | number
    }
  >,
  rngState: number,
): {
  board: LetterBoard
  rngState: number
  spawned: T[]
}
export function placeBoggleSpecialTilesOnSpawnChance<
  T extends SpecialTilePlacement & CellWithTags,
>(
  board: LetterBoard,
  spawned: readonly T[],
  settingsByKind: Record<
    BoggleSpecialTileKind,
    {
      maximumCount: null | number
      spawnChancePercent: null | number
    }
  >,
  rngState: number,
) {
  if (spawned.length === 0) {
    return {
      board,
      rngState,
      spawned: [...spawned],
    }
  }

  const resolvedMaximumCounts = Object.fromEntries(
    BOGGLE_SPECIAL_TILE_KINDS.map(kind => [
      kind,
      settingsByKind[kind].maximumCount === null
        ? Number.POSITIVE_INFINITY
        : clampBoggleSpecialTileCount(
            settingsByKind[kind].maximumCount,
            board.flat().length,
          ),
    ]),
  ) as Record<BoggleSpecialTileKind, number>
  const liveCounts = Object.fromEntries(
    BOGGLE_SPECIAL_TILE_KINDS.map(kind => [
      kind,
      countBoggleSpecialTilesOnBoard(board, kind),
    ]),
  ) as Record<BoggleSpecialTileKind, number>
  const pendingWins = createEmptyBoggleSpecialTileCadenceCounts()
  const nextSpawned = [...spawned]
  let nextBoard: LetterBoard | null = null
  let nextRngState = rngState

  function canPlaceKind(kind: BoggleSpecialTileKind) {
    return liveCounts[kind] < resolvedMaximumCounts[kind]
  }

  function findPendingKind() {
    return (
      BOGGLE_SPECIAL_TILE_KINDS.find(
        kind => pendingWins[kind] > 0 && canPlaceKind(kind),
      ) ?? null
    )
  }

  for (let index = 0; index < spawned.length; index += 1) {
    const entry = nextSpawned[index]!
    const currentBoard = nextBoard ?? board
    const position = getPlacementPosition(entry)
    const cell = position === null ? null : currentBoard[position.row]?.[position.col]

    if (position === null || !cell) {
      continue
    }

    if (hasAnyBoggleSpecialTile(entry) || hasAnyBoggleSpecialTile(cell)) {
      continue
    }

    let kindToPlace = findPendingKind()
    if (kindToPlace === null) {
      for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
        const spawnChancePercent = settingsByKind[kind].spawnChancePercent
        if (spawnChancePercent === null || !canPlaceKind(kind)) {
          continue
        }

        const roll = nextInteger(nextRngState, 100)
        nextRngState = roll.rngState
        if (roll.value < spawnChancePercent) {
          pendingWins[kind] += 1
        }
      }

      kindToPlace = findPendingKind()
    }

    if (kindToPlace === null) {
      continue
    }

    pendingWins[kindToPlace] -= 1
    liveCounts[kindToPlace] += 1

    if (nextBoard === null) {
      nextBoard = cloneBoard(board)
    }

    nextBoard[position.row]![position.col] = {
      ...cell,
      tags: withSpecialTileKind(cell.tags, kindToPlace),
    } satisfies LetterCell
    nextSpawned[index] = {
      ...entry,
      tags: withSpecialTileKind(entry.tags, kindToPlace),
    }
  }

  return {
    board: nextBoard ?? board,
    rngState: nextRngState,
    spawned: nextSpawned,
  }
}

export function placeBoggleSpecialTilesOnSpawnCadence(
  board: LetterBoard,
  spawned: ReadonlyArray<SpecialTilePlacement & CellWithTags>,
  countBeforeSpawn: number,
  everyXTiles: null | number,
  maximumCount: null | number,
  kind: BoggleSpecialTileKind,
  rngState: number,
): {
  board: LetterBoard
  nextSpawnCadenceCount: number
  rngState: number
  spawned: Array<SpecialTilePlacement & CellWithTags>
}
export function placeBoggleSpecialTilesOnSpawnCadence<
  T extends SpecialTilePlacement & CellWithTags,
>(
  board: LetterBoard,
  spawned: readonly T[],
  countBeforeSpawn: number,
  everyXTiles: null | number,
  maximumCount: null | number,
  kind: BoggleSpecialTileKind,
  rngState: number,
): {
  board: LetterBoard
  nextSpawnCadenceCount: number
  rngState: number
  spawned: T[]
}
export function placeBoggleSpecialTilesOnSpawnCadence<
  T extends SpecialTilePlacement & CellWithTags,
>(
  board: LetterBoard,
  spawned: readonly T[],
  countBeforeSpawn: number,
  everyXTiles: null | number,
  maximumCount: null | number,
  kind: BoggleSpecialTileKind,
  rngState: number,
) {
  if (everyXTiles === null || spawned.length === 0) {
    return {
      board,
      nextSpawnCadenceCount: countBeforeSpawn,
      rngState,
      spawned: [...spawned],
    }
  }

  const resolvedMaximumCount =
    maximumCount === null
      ? Number.POSITIVE_INFINITY
      : clampBoggleSpecialTileCount(maximumCount, board.flat().length)
  const liveCount = countBoggleSpecialTilesOnBoard(board, kind)
  if (liveCount >= resolvedMaximumCount) {
    return {
      board,
      nextSpawnCadenceCount: countBeforeSpawn,
      rngState,
      spawned: [...spawned],
    }
  }

  const eligibleSpawned = spawned.filter(
    entry =>
      !hasAnyBoggleSpecialTile(entry) && isEligibleSpecialTilePlacement(board, entry),
  )

  if (eligibleSpawned.length === 0) {
    return {
      board,
      nextSpawnCadenceCount: countBeforeSpawn,
      rngState,
      spawned: [...spawned],
    }
  }

  const totalCadenceHits =
    Math.floor((countBeforeSpawn + eligibleSpawned.length) / everyXTiles) -
    Math.floor(countBeforeSpawn / everyXTiles)

  if (totalCadenceHits <= 0) {
    return {
      board,
      nextSpawnCadenceCount: countBeforeSpawn + eligibleSpawned.length,
      rngState,
      spawned: [...spawned],
    }
  }

  const remainingCapacity = resolvedMaximumCount - liveCount
  const placementCount = Math.min(totalCadenceHits, remainingCapacity)
  const nextSpawnCadenceCount =
    placementCount < totalCadenceHits
      ? everyXTiles * Math.ceil((countBeforeSpawn + 1) / everyXTiles) +
        (placementCount - 1) * everyXTiles
      : countBeforeSpawn + eligibleSpawned.length

  // Cadence still resolves an exact number of hits; the hits themselves are
  // scattered across the eligible fresh tiles so full clears do not always
  // mark the same early spawn slots.
  const placement = placeBoggleSpecialTilesOnBoard(
    board,
    eligibleSpawned,
    placementCount,
    kind,
    rngState,
  )
  const selected = new Set(
    placement.placements
      .map(getPlacementPosition)
      .filter(position => position !== null)
      .map(position => `${position.row}:${position.col}`),
  )

  return {
    board: placement.board,
    nextSpawnCadenceCount,
    rngState: placement.rngState,
    spawned: spawned.map(entry => {
      const position = getPlacementPosition(entry)
      if (position === null || !selected.has(`${position.row}:${position.col}`)) {
        return entry
      }

      return {
        ...entry,
        tags: withSpecialTileKind(entry.tags, kind),
      }
    }),
  }
}

/** Prefer plain letters, then retag tiles that already carry another special tag. */
export function placeBoggleSpecialTilesForResetWhenBoardEmpty(
  board: LetterBoard,
  dimensions: GridDimensions,
  kind: BoggleSpecialTileKind,
  count: number,
  rngState: number,
): {
  board: LetterBoard
  placements: GridPosition[]
  rngState: number
} {
  if (count <= 0) {
    return { board, placements: [], rngState }
  }

  const tierBare: GridPosition[] = []
  const tierConverted: GridPosition[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell || isBoggleSpecialTile(cell, kind)) {
        continue
      }

      if (!hasAnyBoggleSpecialTile(cell)) {
        tierBare.push({ col, row })
      } else {
        tierConverted.push({ col, row })
      }
    }
  }

  const orderedPositions = [...tierBare, ...tierConverted]
  const cappedCount = clampBoggleSpecialTileCount(count, orderedPositions.length)

  const picked = pickDistinctEntries(orderedPositions, cappedCount, rngState)
  const nextBoard = cloneBoard(board)
  const placements = picked.picked

  for (const placement of placements) {
    const cell = nextBoard[placement.row]?.[placement.col]
    if (!cell) {
      continue
    }

    nextBoard[placement.row]![placement.col] = {
      ...cell,
      tags: withSpecialTileKind(cell.tags, kind),
    } satisfies LetterCell
  }

  return {
    board: nextBoard,
    placements,
    rngState: picked.rngState,
  }
}
